<script lang="ts" setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import LoginCard from "./LoginCard.vue";

const router = useRouter();
const baseUrl = `${import.meta.env.VITE_API_URL}/auth`;

type Step = "email" | "otp" | "password" | "done";

const step = ref<Step>("email");
const submitting = ref(false);
const errorMessage = ref("");

// Step 1
const email = ref("");
// Step 2
const otp = ref("");
const codeLength = ref(8);
// Step 3
const newPassword = ref("");
const confirmPassword = ref("");

let continuationToken: string | null = null;

async function submitEmail() {
  errorMessage.value = "";
  submitting.value = true;
  try {
    const response = await fetch(`${baseUrl}/forgot-password/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.value.trim() }),
    });

    if (response.ok) {
      const data = await response.json();
      continuationToken = data.continuationToken ?? null;
      codeLength.value = data.codeLength || 8;
      step.value = "otp";
    } else {
      errorMessage.value = "Unable to send reset code. Please try again.";
    }
  } catch {
    errorMessage.value = "Something went wrong. Please check your connection and try again.";
  } finally {
    submitting.value = false;
  }
}

async function submitOtp() {
  errorMessage.value = "";

  if (!continuationToken) {
    errorMessage.value = "The code you entered is incorrect or has expired. Please try again.";
    return;
  }

  submitting.value = true;
  try {
    const response = await fetch(`${baseUrl}/forgot-password/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ continuationToken, otp: otp.value.trim() }),
    });

    if (response.ok) {
      const data = await response.json();
      continuationToken = data.continuationToken;
      step.value = "password";
    } else {
      errorMessage.value = "The code you entered is incorrect or has expired. Please try again.";
    }
  } catch {
    errorMessage.value = "Something went wrong. Please check your connection and try again.";
  } finally {
    submitting.value = false;
  }
}

async function submitNewPassword() {
  errorMessage.value = "";

  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = "Passwords do not match.";
    return;
  }

  submitting.value = true;
  try {
    const response = await fetch(`${baseUrl}/forgot-password/submit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ continuationToken, newPassword: newPassword.value }),
    });

    if (response.ok) {
      step.value = "done";
    } else {
      errorMessage.value = "Unable to set your new password. Please start the process again.";
    }
  } catch {
    errorMessage.value = "Something went wrong. Please check your connection and try again.";
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <LoginCard title="Reset your password">
    <div v-if="errorMessage" class="alert alert-warning" role="alert">
      {{ errorMessage }}
    </div>

    <!-- Step 1: Email -->
    <form v-if="step === 'email'" @submit.prevent="submitEmail">
      <p class="text-tertiary text-center small mb-3">
        Enter your email address and we'll send you a verification code to reset your password.
      </p>
      <div class="mb-3">
        <input
          type="email"
          class="form-control"
          placeholder="Email"
          v-model="email"
          autocomplete="email"
          required
          :disabled="submitting"
        />
      </div>
      <button type="submit" class="bb-btn-primary-cta w-100" :disabled="submitting">
        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
        Send reset code
      </button>
      <div class="text-center mt-3">
        <router-link to="/login" class="small text-secondary">Back to sign in</router-link>
      </div>
    </form>

    <!-- Step 2: OTP -->
    <form v-else-if="step === 'otp'" @submit.prevent="submitOtp">
      <p class="text-secondary small mb-3">
        We sent a {{ codeLength }}-digit code to <strong>{{ email }}</strong>. Enter it below.
      </p>
      <div class="mb-3">
        <input
          type="text"
          class="form-control text-center otp-input"
          :placeholder="'0'.repeat(codeLength)"
          v-model="otp"
          :maxlength="codeLength"
          inputmode="numeric"
          autocomplete="one-time-code"
          required
          :disabled="submitting"
        />
      </div>
      <button type="submit" class="bb-btn-primary-cta w-100" :disabled="submitting">
        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
        Verify code
      </button>
      <div class="text-center mt-3">
        <a href="#" class="small text-secondary" @click.prevent="step = 'email'">Resend code</a>
      </div>
    </form>

    <!-- Step 3: New password -->
    <form v-else-if="step === 'password'" @submit.prevent="submitNewPassword">
      <p class="text-secondary text-center small mb-3">
        Choose a new password for your account.
      </p>
      <div class="mb-3">
        <input
          type="password"
          class="form-control"
          placeholder="New password"
          v-model="newPassword"
          autocomplete="new-password"
          required
          :disabled="submitting"
        />
      </div>
      <div class="mb-3">
        <input
          type="password"
          class="form-control"
          placeholder="Confirm new password"
          v-model="confirmPassword"
          autocomplete="new-password"
          required
          :disabled="submitting"
        />
      </div>
      <button type="submit" class="bb-btn-primary-cta w-100" :disabled="submitting">
        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
        Set new password
      </button>
    </form>

    <!-- Step 4: Done -->
    <div v-else-if="step === 'done'" class="text-center">
      <i class="bi bi-check-circle text-success" style="font-size: 2.5rem;"></i>
      <p class="mt-3 mb-1 fw-semibold">Password updated</p>
      <p class="text-secondary small mb-4">You can now sign in with your new password.</p>
      <router-link to="/login" class="bb-btn-primary-cta w-100">Back to sign in</router-link>
    </div>
  </LoginCard>
</template>

<style lang="scss" scoped>
:deep(.login-card-body) {
  min-height: 26em;
}

.otp-input {
  font-size: 1.5rem;
  letter-spacing: 0.4em;
}


</style>
